import { onLog } from 'firebase/app'
import { Capacitor } from '@capacitor/core'
import { db } from './firebase'
import { terminate } from './firestore'
import { onIndexedDbBreakage } from './idbBreakage'
import { log } from './logger'
import { bootMilestones } from './perf'
import { Sentry } from './sentry'
import { WebViewRestart, canRestartWebView, isRestartInFlight, markRestartInFlight, saveRestorePath } from './webViewRestart'

// WebKit's IndexedDB connection can break under Firestore (mostly iOS), which
// trips an internal assertion and leaves every Firestore call failing for the
// rest of the session. A fresh WebView reopens IndexedDB and recovers it, and
// the persistent cache survives — offline data stays. If restarting keeps
// failing, stop and report so we can decide whether wiping the cache is needed.
// Any operation that rejects inside Firestore's AsyncQueue is logged as
// "INTERNAL UNHANDLED ERROR" and latches the queue as failed — every later call
// then throws the b815 assertion. Catching the first log restarts ~10s sooner
// than waiting for the assertion; the assertion stays as a fallback.
// WebKit can also break IndexedDB mid-transaction without the AsyncQueue ever
// logging it (#583), so an IDB breakage seen by Sentry restarts too — gated on
// auth still being unresolved 15s later, since the signal is broad.
const QUEUE_FAILED = ['INTERNAL UNHANDLED ERROR', 'INTERNAL ASSERTION FAILED']
// Matches sessionDiagnostics' follow-up timer, so the follow-up still reports
// the dead state, and gives a slow auth boot time to resolve.
const IDB_LIVENESS_MS = 15_000
const RESTARTS_KEY = 'fs_recovery_restarts'
const MAX_RESTARTS = 3
const WINDOW_MS = 60 * 60 * 1000

let handled = false

function recentRestarts(now: number): number[] {
  try {
    const stored = JSON.parse(localStorage.getItem(RESTARTS_KEY) ?? '[]') as unknown
    return Array.isArray(stored) ? stored.filter((t): t is number => typeof t === 'number' && now - t < WINDOW_MS) : []
  } catch {
    return []
  }
}

async function restart() {
  markRestartInFlight('recovery_restart')
  saveRestorePath()
  await Sentry.flush(2000)
  await terminate(db).catch(() => {})
  if (canRestartWebView()) {
    await WebViewRestart.restart()
    return
  }
  // An in-process reload on iOS can hang on IndexedDB; take the memory-cache
  // boot path ota.ts uses for the same reason (see isOtaBoot in firebase.ts).
  if (Capacitor.getPlatform() === 'ios') localStorage.setItem('ota_pending_reload', '1')
  window.location.reload()
}

function recover(trigger: 'queue_failure' | 'idb_breakage', detail: string) {
  if (handled || isRestartInFlight()) return
  handled = true

  const now = Date.now()
  const restarts = recentRestarts(now)
  if (restarts.length >= MAX_RESTARTS) {
    log('fs-recovery', 'giving up — restart limit reached', { restarts: restarts.length })
    Sentry.captureMessage(`Firestore recovery gave up after ${restarts.length} restarts in 1h`, {
      level: 'error',
      tags: { recovery_trigger: trigger },
      fingerprint: ['firestore-recovery-gave-up', trigger],
      extra: { trigger, detail },
    })
    return
  }

  localStorage.setItem(RESTARTS_KEY, JSON.stringify([...restarts, now]))
  log('fs-recovery', `restarting after ${trigger}`, { attempt: restarts.length + 1 })
  Sentry.captureMessage('Firestore recovery restart', {
    level: 'warning',
    tags: { recovery_trigger: trigger },
    // Default grouping splits this per release; one issue per trigger makes repeats countable.
    fingerprint: ['firestore-recovery-restart', trigger],
    extra: { attempt: restarts.length + 1, trigger, detail },
  })
  void restart().catch(err => log('fs-recovery', 'restart failed', { error: String(err) }))
}

export function installFirestoreRecovery() {
  onLog(({ type, message }) => {
    if (type === '@firebase/firestore' && QUEUE_FAILED.some(m => message.includes(m))) recover('queue_failure', message.slice(0, 300))
  }, { level: 'error' })

  onIndexedDbBreakage(detail => {
    log('fs-recovery', 'idb breakage detected', { detail })
    setTimeout(() => {
      const m = bootMilestones()
      if (m.authResolved) {
        log('fs-recovery', 'idb breakage but auth resolved — no restart', m)
        return
      }
      recover('idb_breakage', `${detail} | firstSnapshot=${m.firstSnapshot}`)
    }, IDB_LIVENESS_MS)
  })
}
